import { View } from "@/components/view/view";
import React, { useEffect, useMemo, useRef, useState } from "react";
import DemoSignupLayout from "./demoSignupLayout";
import { Text } from "@/components/text/text";
import ShakeEffectView from "@/components/animatableView/shakeEffectView";
import { FormikProvider, useFormik } from "formik";
import ValidationIndicator from "@/components/validationIndicator/validationIndicator";
import { createValidator, DataCy, DoorLoopLeadDto, DoorLoopLeadSources, SegmentEventTypes } from "@/dto";
import { useHistory, useLocation, useParams } from "react-router-dom";
import noop from "lodash/noop";
import set from "lodash/set";
import pick from "lodash/pick";
import isEmpty from "lodash/isEmpty";
import intersection from "lodash/intersection";
import keys from "lodash/keys";
import qs from "query-string";
import { analyticsService } from "services/analyticsService";
import { useInjectClickCaseScript } from "./clickCeaseScript";
import DLButton from "@/components/button/buttonUI";
import { DLButtonColorsEnum, DLButtonSizesEnum } from "@/components/button/enums";
import { useResponsiveHelper } from "@/contexts/responsiveContext";
import ViewSlider from "react-view-slider";
import { useEffectAsync } from "hooks/useEffectAsync";
import { Link } from "@/components/link/link";
import { MobileScreenContainerDefaultPadding } from "contexts/utils";
import { useAnalyticsService } from "hooks/useAnalyticsService";
import { demoFormUtils, getDLMCCookie, getSalesforceUuid } from "./demo.utils";
import { computeLeadScoreBreakdown } from "./leadScoreCalculator";
import { DEMO_FORM_VERSION_QUERY_PARAM, useDemoFormVersion } from "@/hooks/useDemoFormVersion";
import { axiosInstance } from "./axiosInstance";
import { DEMO_ADDITIONAL_INFO_BASE_ROUTE, DEMO_SCHEDULE_ROUTE } from "@/components/router";
import { ContactDetailsStep } from "@/pages/demo/steps/contactDetailsStep";
import { UserDetailsStep } from "@/pages/demo/steps/userDetailsStep";
import { ScheduleDemoStep } from "./steps/scheduleDemoStep";
import { useFormStorage } from "@/pages/demo/hooks/useFormStorage";
import { WizardStepper } from "components/wizard";
import { FORM_INNER_WIDTH, STEPPER_LABELS, STEPPER_LABELS_TWO_STEPS_DEMO_FORM } from "@/pages/demo/demoForm";
import {
  PARAM_TO_VIEW_MAP,
  PARAM_TO_VIEW_MAP_TWO_STEPS,
  StepId,
  VIEW_TO_PARAM_MAP,
  ViewNamesTwoSteps
} from "@/pages/demo/constants/views";
import { HUBSPOT_CONTACT_DETAILS_FORM_ID, HUBSPOT_USER_DETAILS_FORM_ID } from "./constants/hubspot";
import { AllDetailsStep } from "./steps/allDetailsStep";

interface IPDetails {
  country: string;
  isUsingVPN: boolean;
  ip: string;
}

// Define a type that extends DoorLoopLeadDto with UTM parameters
interface ExtendedDoorLoopLeadDto extends DoorLoopLeadDto {
  utm_source?: string;
  utm_term?: string;
  utm_medium?: string;
  utm_content?: string;
  utm_campaign?: string;
}

const initialQueryParamsMap: Record<string, keyof ExtendedDoorLoopLeadDto> = {
  email: "email",
  full_name: "name",
  phone: "phone",
  expected_number_of_units__c: "numberOfUnits",
  primary_portfolio__type: "mainPropertyType",
  customer_reported_timeline__c: "whenPlanToUseSoftware",
  user_selected_occupation: "selectedJobTitle",
  utm_source: "utm_source",
  utm_term: "utm_term",
  utm_medium: "utm_medium",
  utm_content: "utm_content",
  utm_campaign: "utm_campaign"
};

const PRIVACY_POLICY_URL = "https://www.doorloop.com/legal/privacy";
const TERMS_OF_SERVICE_URL = "https://www.doorloop.com/legal/terms";

const PROXYCHECK_TIMEOUT_MS = 4000;

const getIPDetails = async (): Promise<IPDetails | null> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PROXYCHECK_TIMEOUT_MS);
  demoFormUtils.track("geo_detection_started", { provider: "proxycheck.io" });
  try {
    const proxyResponse = await fetch(`https://proxycheck.io/v2/?key=public-4g8i9m-1696rc-e43638&risk=1&vpn=1&asn=1`, {
      method: "GET",
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    if (!proxyResponse.ok) {
      demoFormUtils.track("geo_detection_failed", {
        provider: "proxycheck.io",
        reason: "http_error",
        status: proxyResponse.status
      });
      return null;
    }
    const data = await proxyResponse.json();
    if (!data || data.status !== "ok") {
      demoFormUtils.track("geo_detection_failed", { provider: "proxycheck.io", reason: "non_ok_status" });
      return null;
    }
    const RESERVED_KEYS = new Set(["status", "node", "message"]);
    const ipKey = Object.keys(data).find((k) => !RESERVED_KEYS.has(k));
    if (!ipKey || typeof data[ipKey] !== "object") {
      demoFormUtils.track("geo_detection_failed", { provider: "proxycheck.io", reason: "no_ip_key" });
      return null;
    }
    const ipDetails = data[ipKey];
    demoFormUtils.track("geo_detection_succeeded", {
      provider: "proxycheck.io",
      country: ipDetails.country ?? "",
      proxy: ipDetails.proxy ?? "no"
    });
    return {
      country: ipDetails.country ?? "",
      isUsingVPN: ipDetails.proxy === "yes",
      ip: ipKey
    };
  } catch (error) {
    clearTimeout(timeoutId);
    demoFormUtils.track("geo_detection_failed", {
      provider: "proxycheck.io",
      reason: error instanceof Error && error.name === "AbortError" ? "timeout" : "network_error",
      error_message: error instanceof Error ? error.message : String(error)
    });
    return null;
  }
};

const getCurrentActiveView = (stepId?: StepId, pathname?: string): ViewNamesTwoSteps => {
  if (pathname && pathname.startsWith(DEMO_SCHEDULE_ROUTE)) {
    return ViewNamesTwoSteps.ScheduleDemo;
  }
  if (!stepId) {
    return ViewNamesTwoSteps.AllDetails;
  }
  return PARAM_TO_VIEW_MAP_TWO_STEPS[stepId];
};

export function DemoAdditionalInfoTwoSteps() {
  const { isMobile } = useResponsiveHelper();
  const [showErrorShakeEffect, setShowErrorShakeEffect] = useState(false);
  const [arrivedWithInvalidEmail, setArrivedWithInvalidEmail] = useState(false);
  const [loadingInProgress, setLoadingInProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { demoFormVersionValues, version } = useDemoFormVersion();

  const location = useLocation();
  const { stepId } = useParams<{ stepId?: StepId }>();
  const initialView = getCurrentActiveView(stepId, location.pathname);
  const [currentActiveView, setCurrentActiveView] = useState<ViewNamesTwoSteps>(initialView);
  const fireIntercomEventHandler = useRef<() => void>(noop);
  const IPDetailsRef = useRef<IPDetails | null>(null);
  const viewSliderContentRef = useRef<HTMLDivElement | null>(null);
  // Tracks whether the component is still mounted, so post-await state
  // writes inside useEffectAsync don't fire on an unmounted component.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Eagerly capture ?salesforce_uuid= from the URL at mount, BEFORE
  // navigateToStep strips the query string via history.push({ pathname }).
  useEffect(() => {
    getSalesforceUuid();
  }, []);
  const [animatedViewHeightPx, setAnimatedViewHeightPx] = useState<number | undefined>(undefined);
  const { startSessionRecordingWithoutFeatureFlag } = useAnalyticsService();

  const history = useHistory();
  const { formData, saveStepData } = useFormStorage();

  const hadEmailInQueryString = useMemo(() => {
    const { email } = qs.parse(window.location.search);
    return Boolean(email);
  }, []);

  const initFormValues = useMemo(() => {
    const signupDto = new DoorLoopLeadDto();
    const queryString = qs.parse(window.location.search);
    signupDto.email ||= queryString.email as string;

    if (formData.contactDetailsStep) {
      Object.assign(signupDto, { ...formData.contactDetailsStep, ...formData.userDetailsStep });
    }

    Object.entries(initialQueryParamsMap).forEach(([queryKey, fieldName]) => {
      if (queryString[queryKey]) {
        const value = fieldName === "numberOfUnits" ? Number(queryString[queryKey]) : queryString[queryKey];
        Object.assign(signupDto, { [fieldName]: value });
      }
    });

    return signupDto;
  }, [formData]);

  const formik = useFormik<DoorLoopLeadDto>({
    initialValues: initFormValues,
    validate: createValidator(DoorLoopLeadDto),
    onSubmit: noop,
    validateOnBlur: true,
    validateOnChange: true
  });

  useEffect(() => {
    const measureEl = viewSliderContentRef.current;
    if (!measureEl) return;
    const computeAndSetAnimatedHeight = () => setAnimatedViewHeightPx(measureEl.getBoundingClientRect().height);
    computeAndSetAnimatedHeight();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => computeAndSetAnimatedHeight());
      ro.observe(measureEl);
      return () => ro.disconnect();
    }
  }, [currentActiveView]);

  useEffect(() => {
    fireIntercomEventHandler.current = () => {
      demoFormUtils.track("user_clicked_intercom_bubble", { step: currentActiveView });
    };
  }, [currentActiveView]);

  useEffect(() => {
    analyticsService.page();
  }, []);

  useEffect(() => {
    const activeView = getCurrentActiveView(stepId, location.pathname);
    if (activeView !== currentActiveView) {
      setCurrentActiveView(activeView);
    }
  }, [stepId, location.pathname]);

  useEffectAsync(async () => {
    const formValues = formik.values;
    const email = formValues?.email;

    if (email) {
      analyticsService.identifyLead(
        email,
        pick(formValues, ["name", "phone", "numberOfUnits"]) as Record<string, string>
      );
      demoFormUtils.track(
        "lead_created",
        {
          lead_created_location: window.location.pathname,
          lead_created_pms_post: false
        },
        { email }
      );
    }

    const leadSource =
      new URLSearchParams(window.location.search).get("leadsource") === "Scheduled Call"
        ? DoorLoopLeadSources.SCHEDULE_CALL
        : DoorLoopLeadSources.SCHEDULE_DEMO;
    formik.setFieldValue("leadSource", leadSource);

    let ipDetails: IPDetails | null = null;
    try {
      const res = await getIPDetails();
      if (!isMountedRef.current) return;
      if (res) {
        ipDetails = res;
        IPDetailsRef.current = res;
        const { ip, country, isUsingVPN } = res;

        formik.setFieldValue("ipAddress", ip);
        formik.setFieldValue("country", country);
        formik.setFieldValue("isUsingVPN", isUsingVPN);
      }
    } catch (error) {
      // getIPDetails already tracks geo_detection_failed internally.
    }

    if (!isMountedRef.current) return;

    try {
      const emailValidationResult = await demoFormUtils.hubspot.createEmailOnlyLead({
        ...formValues,
        leadSource,
        ipAddress: ipDetails?.ip,
        country: ipDetails?.country,
        isUsingVPN: ipDetails?.isUsingVPN,
        posthog_distinct_id: window.posthog?.get_distinct_id?.()
      });
      if (!isMountedRef.current) return;

      if (
        emailValidationResult &&
        emailValidationResult.ok === false &&
        emailValidationResult.reason === "invalid_email"
      ) {
        formik.setFieldError("email", "Please enter a valid email");
        formik.setFieldTouched("email", true, false);
        setArrivedWithInvalidEmail(true);
      }
    } catch (error) {
      // Genuinely unexpected — let the user proceed.
    }

    if (isMountedRef.current) {
      startSessionRecordingWithoutFeatureFlag();
    }
  }, []);

  useInjectClickCaseScript();

  const runShakeEffect = () => {
    setShowErrorShakeEffect(true);
    setTimeout(() => {
      setShowErrorShakeEffect(false);
    }, 300);
  };

  const isAllDetailsValid = async () => {
    // Touch all required fields
    const allRequiredFields = [
      "email",
      "name",
      "phone",
      "mainPropertyType",
      "selectedJobTitle",
      "jobTitle",
      "numberOfUnits",
      "whenPlanToUseSoftware"
    ];

    allRequiredFields.forEach((field) => {
      formik.setFieldTouched(field);
    });

    const [errors, { isValid: isEmailValid }] = await Promise.all([
      formik.validateForm(),
      demoFormUtils.validateEmail(formik.values.email)
    ]);

    if (!isEmailValid) {
      formik.setFieldError("email", "Please enter a valid email");
      formik.setFieldTouched("email", true, false);
    }

    const valid = isEmailValid && isEmpty(intersection(keys(errors), allRequiredFields));

    if (valid) {
      // Save both steps data
      saveStepData("contactDetailsStep", pick(formik.values, ["email", "name", "phone", "mainPropertyType"]));
      saveStepData(
        "userDetailsStep",
        pick(formik.values, [
          "selectedJobTitle",
          "jobTitle",
          "doYouManagePropertiesInTheUSOrCanada",
          "numberOfUnits",
          "whenPlanToUseSoftware"
        ])
      );
    }

    return valid;
  };

  const didPressScheduleDemo = async (values: DoorLoopLeadDto) => {
    setLoadingInProgress(true);

    try {
      // Validate all details at once since it's now a single step
      const isValid = await isAllDetailsValid();

      if (!isValid) {
        runShakeEffect();
        return;
      }

      // Track form completion
      const allFormValues = pick(values, [
        "name",
        "phone",
        "mainPropertyType",
        "numberOfUnits",
        "selectedJobTitle",
        "whenPlanToUseSoftware"
      ]) as Record<string, string>;

      demoFormUtils.track("lead_filled_out_complete_demo_form", allFormValues, allFormValues);
      analyticsService.identifyLead(values.email || "", allFormValues);

      // Report to HubSpot with both forms. Capture the promises so we can
      // await them before any window.location.href redirect — without that,
      // the browser cancels the in-flight POSTs and Pt.2 fields never land
      // on the HubSpot contact for redirect-disqualified leads (Tenant,
      // sanctioned countries, low score).
      const hubspotPt1Promise = demoFormUtils.hubspot.reportToHubSpot(values, HUBSPOT_CONTACT_DETAILS_FORM_ID);
      const hubspotPt2Promise = demoFormUtils.hubspot.reportToHubSpot(values, HUBSPOT_USER_DETAILS_FORM_ID);

      // Compute lead score ONCE here with full breakdown — fires the
      // pass/fail event exactly once per submission.
      const leadScoreBreakdown = computeLeadScoreBreakdown(values, getDLMCCookie());
      const leadScoreEventName = leadScoreBreakdown.passed ? "lead_passed_lead_scoring" : "lead_failed_lead_scoring";
      demoFormUtils.track(
        leadScoreEventName,
        {
          leadScore: leadScoreBreakdown.total,
          unitsScore: leadScoreBreakdown.components.units,
          sourceScore: leadScoreBreakdown.components.source,
          timelineScore: leadScoreBreakdown.components.timeline,
          propertyTypeScore: leadScoreBreakdown.components.propertyType,
          roleScore: leadScoreBreakdown.components.role,
          mainPropertyType: leadScoreBreakdown.inputs.mainPropertyType ?? "",
          selectedJobTitle: leadScoreBreakdown.inputs.selectedJobTitle ?? "",
          whenPlanToUseSoftware: leadScoreBreakdown.inputs.whenPlanToUseSoftware ?? "",
          utmSource: leadScoreBreakdown.inputs.utmSource ?? "",
          numberOfUnits: leadScoreBreakdown.inputs.numberOfUnits
        },
        { leadScore: leadScoreBreakdown.total }
      );

      // Single ad-conversion fanout (PostHog + gtag + fbq + uetq) under
      // the lead_submitted_personal_info_form name, with no PII spread.
      const cleanSegmentPayload = pick(values, [
        "primary_portfolio__type",
        "customer_reported_timeline__c",
        "numberOfUnits",
        "selectedJobTitle",
        "mainPropertyType",
        "whenPlanToUseSoftware"
      ]) as Record<string, string>;
      analyticsService.track(SegmentEventTypes.LEAD_SUBMITTED_PERSONAL_INFO_FORM, {
        category: "Leads",
        label: "Demo",
        custom_lead_score: leadScoreBreakdown.total,
        ...cleanSegmentPayload
      });

      setErrorMessage(undefined);

      const { result_url, result_type } = await demoFormUtils.getCalendlyURL(values);

      if (result_type === "arbitrary_url") {
        demoFormUtils.track("routing_redirect", { url: result_url, leadScore: leadScoreBreakdown.total });
        // Await both HubSpot POSTs (with a 5s safety timeout) before
        // navigating away. Without this, the browser cancels in-flight
        // requests, leaving Pt.1 + Pt.2 fields empty on the HubSpot contact
        // for redirect-disqualified leads.
        await Promise.race([
          Promise.all([hubspotPt1Promise, hubspotPt2Promise]),
          new Promise<void>((resolve) => setTimeout(resolve, 5000))
        ]);
        window.location.href = result_url;
      } else {
        demoFormUtils.track("routing_embed_calendly", {
          url: result_url,
          leadScore: leadScoreBreakdown.total,
          numberOfUnits: leadScoreBreakdown.inputs.numberOfUnits
        });
        const params = new URLSearchParams({
          result_url,
          [DEMO_FORM_VERSION_QUERY_PARAM]: version
        });
        navigateToStep(ViewNamesTwoSteps.ScheduleDemo, params);
      }
    } finally {
      setLoadingInProgress(false);
    }
  };

  const navigateToStep = (viewId: ViewNamesTwoSteps, searchParams?: URLSearchParams) => {
    setCurrentActiveView(viewId);

    if (viewId === ViewNamesTwoSteps.ScheduleDemo) {
      const stringifyParams = searchParams?.toString();

      history.push({
        pathname: DEMO_SCHEDULE_ROUTE,
        search: searchParams ? stringifyParams : undefined
      });
      return;
    }

    const currentParams = qs.parse(location.search);
    const preservedParams = new URLSearchParams();

    if (currentParams.result_url && typeof currentParams.result_url === "string") {
      const resultUrl = new URL(decodeURIComponent(currentParams.result_url));

      resultUrl.searchParams.forEach((value, key) => {
        preservedParams.set(key, value);
      });
    }

    if (
      currentParams[DEMO_FORM_VERSION_QUERY_PARAM] &&
      typeof currentParams[DEMO_FORM_VERSION_QUERY_PARAM] === "string"
    ) {
      preservedParams.set(DEMO_FORM_VERSION_QUERY_PARAM, currentParams[DEMO_FORM_VERSION_QUERY_PARAM]);
    }

    history.push({
      pathname: `${DEMO_ADDITIONAL_INFO_BASE_ROUTE}/${VIEW_TO_PARAM_MAP[viewId]}`,
      search: preservedParams.toString() || undefined
    });
  };

  const didPressPrivacyPolicy = async () => {
    demoFormUtils.track("user_clicked_privacy_policy", { step: currentActiveView });
  };

  const viewsArray = [
    () => <AllDetailsStep shouldDisplayEmail={!hadEmailInQueryString || arrivedWithInvalidEmail} />,
    () => <ScheduleDemoStep />
  ];

  const documentTitle = useMemo(() => {
    if (currentActiveView === ViewNamesTwoSteps.ScheduleDemo) {
      return "Schedule Demo - Schedule";
    }
    return "Schedule Demo - Additional Info";
  }, [currentActiveView]);

  return (
    <DemoSignupLayout
      hideMarketingView={currentActiveView === ViewNamesTwoSteps.ScheduleDemo ? false : true}
      documentTitle={documentTitle}
      gap={0}
    >
      <FormikProvider value={formik}>
        <View paddingLeft={isMobile ? 0 : 40} paddingRight={isMobile ? 0 : 40}>
          <View
            paddingLeft={isMobile ? 10 : 40}
            paddingRight={isMobile ? 10 : 40}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <WizardStepper
              activeStep={currentActiveView}
              wizardSteps={STEPPER_LABELS_TWO_STEPS_DEMO_FORM}
              setActiveStep={navigateToStep}
            />
            {currentActiveView !== ViewNamesTwoSteps.ScheduleDemo && (
              <>
                <Text
                  fontSize={30}
                  fontWeight={900}
                  textTransform={"uppercase"}
                  value={"REQUEST A DEMO"}
                  marginTop={20}
                />

                <Text fontSize={20} value={demoFormVersionValues.firstStep.subheader} marginTop={10} align={"center"} />
              </>
            )}
            <div
              className="w-full overflow-hidden transition-[height] duration-500 ease-out will-change-[height]"
              style={{ height: animatedViewHeightPx === undefined ? "auto" : animatedViewHeightPx }}
            >
              <div ref={viewSliderContentRef}>
                <ViewSlider
                  className="w-full"
                  renderView={({ index }) => viewsArray[index]?.()}
                  numViews={2}
                  animateHeight={false}
                  activeView={currentActiveView}
                  innerViewWrapper={{ display: "block", width: "100%" }}
                />
              </div>
            </div>
            {currentActiveView !== ViewNamesTwoSteps.ScheduleDemo && (
              <ShakeEffectView fullWidth showEffect={showErrorShakeEffect}>
                <View alignItems={"center"} marginTop={20}>
                  <DLButton
                    onClick={async () => await didPressScheduleDemo(formik.values)}
                    actionText={"Continue"}
                    size={DLButtonSizesEnum.LARGE}
                    isLoading={loadingInProgress}
                    style={{ width: "100%" }}
                    color={DLButtonColorsEnum.SUCCESS}
                    dataCy={DataCy.marketing.demoForm.continueButton}
                  />
                </View>
              </ShakeEffectView>
            )}
            {currentActiveView !== ViewNamesTwoSteps.ScheduleDemo && (
              <View
                flexDirection={"column"}
                marginTop={20}
                paddingLeft={MobileScreenContainerDefaultPadding}
                paddingRight={MobileScreenContainerDefaultPadding}
                justifyContent={"center"}
                alignItems={"center"}
              >
                <View flexDirection={"row"} marginTop={8} justifyContent={"center"}>
                  <Text fontSize={12} value={"By continuing, you agree to DoorLoop's"} />
                  <Link
                    hrefUrl={PRIVACY_POLICY_URL}
                    underline={"always"}
                    hoverColor={"blue"}
                    type={"blank"}
                    onClick={async () => await didPressPrivacyPolicy()}
                  >
                    <Text marginLeft={3} fontSize={12} className="lg:!whitespace-nowrap">
                      Privacy Policy
                    </Text>
                  </Link>
                  <Text marginLeft={3} fontSize={12} value={"&"} />
                  <Link hrefUrl={TERMS_OF_SERVICE_URL} underline={"always"} hoverColor={"blue"} type={"blank"}>
                    <Text marginLeft={3} fontSize={12} className="lg:!whitespace-nowrap">
                      Terms
                    </Text>
                  </Link>
                  <Text fontSize={12} className="lg:!whitespace-nowrap">
                    .
                  </Text>
                </View>
              </View>
            )}
            <ValidationIndicator
              shouldShow={errorMessage !== undefined}
              displayText={errorMessage || ""}
              wrapperWidth={FORM_INNER_WIDTH + "px"}
            />
          </View>
        </View>
      </FormikProvider>
    </DemoSignupLayout>
  );
}
